import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export function useFeaturedProperties(limit = 6) {
  const [featuredProperties, setFeaturedProperties] = useState<any[]>([]);
  const [recentProperties, setRecentProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      setError(null);

      try {
        // Check if Supabase is initialized properly
        if (!supabase) {
          throw new Error('Connessione al database non disponibile');
        }

        // Get published status id
        const { data: statusesData, error: statusesError } = await supabase
          .from('property_statuses')
          .select('id, name');

        if (statusesError) throw statusesError;
        
        // Find published status or use a fallback approach
        let publishedStatusId = null;
        if (statusesData && statusesData.length > 0) {
          const publishedStatus = statusesData.find(status => status.name === 'published');
          if (publishedStatus) {
            publishedStatusId = publishedStatus.id;
          } else {
            console.warn('Status "published" not found, using first available status');
            publishedStatusId = statusesData[0].id;
          }
        }

        let propertiesQuery = supabase
          .from('properties')
          .select(`
            *,
            property_images(*),
            zones(*),
            provinces(*)
          `);

        // Add status filter only if we found a valid status
        if (publishedStatusId) {
          propertiesQuery = propertiesQuery.eq('status_id', publishedStatusId);
        }

        // Fetch featured properties
        const { data: featured, error: featuredError } = await propertiesQuery
          .eq('featured', true)
          .limit(limit);

        if (featuredError) throw featuredError;

        // Fetch recent properties if no featured properties
        let recent: any[] = [];
        if (!featured || featured.length === 0) {
          let recentQuery = supabase
            .from('properties')
            .select(`
              *,
              property_images(*),
              zones(*),
              provinces(*)
            `);

          // Add status filter only if we found a valid status  
          if (publishedStatusId) {
            recentQuery = recentQuery.eq('status_id', publishedStatusId);
          }

          const { data: recentData, error: recentError } = await recentQuery
            .order('created_at', { ascending: false })
            .limit(limit);

          if (recentError) throw recentError;
          recent = recentData || [];
        }

        // Transform data to include primary image URL
        const transformedFeatured = (featured || []).map(preparePropertyData);
        const transformedRecent = (recent || []).map(preparePropertyData);
        
        setFeaturedProperties(transformedFeatured);
        setRecentProperties(transformedRecent);
      } catch (err: any) {
        console.error('Error fetching properties:', err);
        setError(err.message || 'Errore nel caricamento degli immobili');
        
        // Set empty arrays to prevent UI breakage
        setFeaturedProperties([]);
        setRecentProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [limit]);

  // Helper to prepare property data with images
  const preparePropertyData = (property: any) => {
    // Sort images by is_primary and position
    const sortedImages = [...(property.property_images || [])].sort((a, b) => {
      if (a.is_primary && !b.is_primary) return -1;
      if (!a.is_primary && b.is_primary) return 1;
      return (a.position || 0) - (b.position || 0);
    });

    return {
      ...property,
      images: sortedImages.map(img => img.url) || [],
      primaryImage: sortedImages.length > 0 ? sortedImages[0].url : null
    };
  };

  return {
    featuredProperties,
    recentProperties,
    loading,
    error,
    // Determina quali immobili mostrare: featured se ce ne sono, altrimenti recent
    displayProperties: featuredProperties.length > 0 ? featuredProperties : recentProperties
  };
}